/* Copyright (c) 2020-present CloudIO Inc */

import { IoPagesHistory } from '@cloudio-saas/datasource-types';
import { useStateXAtomValue } from '@cloudio-saas/statex';
import { DiffEditor, Monaco } from '@monaco-editor/react';
import { Dialog, IconButton, Toolbar, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { pageAtom } from 'designer/atoms';
import Icon from 'icons/Icon';
import stringify from 'json-stable-stringify';
import type { editor } from 'monaco-editor';
import { usePlatform } from 'platform/PlatformHooks';
import React, { useState } from 'react';
import { useStoreCurrentRecord } from 'store/useAppStore';
import useDataStore from 'store/useDataStore';
import { useIoPagesStore } from 'store/utils/utils';
import { useTheme } from 'theme/Theme';
import { selectionAtom } from './PagesHistory';

export default function PagesHistoryDiffDialog({
  onClose,
  appUid,
}: {
  appUid: string;
  onClose: () => void;
}): React.ReactElement | null {
  const store = useDataStore<IoPagesHistory>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPagesHistory',
    alias: 'IoPagesHistory-diff',
  });
  const pageStore = useIoPagesStore(appUid);
  const { record: rec } = useStoreCurrentRecord(pageStore);
  const selection = useStateXAtomValue(selectionAtom);
  const theme = useTheme();
  const [navi, setNavi] = useState<editor.IDiffNavigator | null>(null);
  const platform = usePlatform();

  function onMount(_editor: editor.IStandaloneDiffEditor, monaco: Monaco) {
    const _navi = monaco.editor.createDiffNavigator(_editor, {
      followsCaret: true,
      ignoreCharChanges: true,
    });
    setNavi(_navi);
  }

  const options: editor.IStandaloneEditorConstructionOptions = {
    folding: true,
    lineNumbers: 'on' as const,
    minimap: { enabled: false },
    readOnly: true,
    scrollBeyondLastLine: false,
    selectOnLineNumbers: true,
  };

  if (!rec) {
    return <>Missing rec!</>;
  }
  let original = '';
  let modified = '';
  let title = '';
  const twoSelection = selection[0] && selection[1];
  const hasSelection = selection[0] || selection[1];
  if (twoSelection) {
    store.read({
      props: {
        filter: [
          { appUid: { is: rec.appUid } },
          { pageUid: { is: rec.pageUid } },
          {
            anyOf: [
              { version: { is: selection[0] ?? '' } },
              { version: { is: selection[1] ?? '' } },
            ],
          },
        ],
        projection: {
          md: 1,
          version: 1,
          lastUpdatedBy: 1,
          lastUpdateDate: 1,
        },
        limit: 2,
      },
    });

    const records = Object.values(store.records());
    if (records.length === 2) {
      if (records[0].version === selection[0]) {
        original = stringify(records[0].md, { space: '  ' });
        modified = stringify(records[1].md, { space: '  ' });
        title = `${formatDistanceToNow(
          new Date(records[0].lastUpdateDate ?? ''),
          {
            addSuffix: true,
            includeSeconds: true,
          },
        )} by ${records[0].lastUpdatedBy} - ${formatDistanceToNow(
          new Date(records[1].lastUpdateDate ?? ''),
          {
            addSuffix: true,
            includeSeconds: true,
          },
        )} by ${records[1].lastUpdatedBy}`;
      } else {
        original = stringify(records[1].md, { space: '  ' });
        modified = stringify(records[0].md, { space: '  ' });
        title = `${formatDistanceToNow(
          new Date(records[1].lastUpdateDate ?? ''),
          {
            addSuffix: true,
            includeSeconds: true,
          },
        )} by ${records[1].lastUpdatedBy} - ${formatDistanceToNow(
          new Date(records[0].lastUpdateDate ?? ''),
          {
            addSuffix: true,
            includeSeconds: true,
          },
        )} by ${records[0].lastUpdatedBy}`;
      }
      if (original === modified) {
        platform.showInfo('No changes found!');
        return null;
      }
    } else {
      title = 'Missing selected records!';
    }
  } else if (hasSelection && platform.pageId === rec.pageUid) {
    const version = selection[0] || selection[1];
    if (!version) return null;
    store.read({
      props: {
        filter: [
          { appUid: { is: rec.appUid } },
          { pageUid: { is: rec.pageUid } },
          { version: { is: version } },
        ],
        projection: {
          md: 1,
          version: 1,
          lastUpdatedBy: 1,
          lastUpdateDate: 1,
        },
        limit: 1,
      },
    });
    const records = Object.values(store.records());
    if (records.length === 1) {
      const page = platform.getter(pageAtom, {
        params: {
          appUid: rec.appUid as string,
          pageId: rec.pageUid as string,
        },
      });

      original = stringify(records[0].md, { space: '  ' });
      modified = stringify(page, { space: '  ' });
      if (original === modified) {
        platform.showInfo('No changes found!');
        return null;
      }
      title = `${formatDistanceToNow(
        new Date(records[0].lastUpdateDate ?? ''),
        {
          addSuffix: true,
          includeSeconds: true,
        },
      )} by ${records[0].lastUpdatedBy} - current page`;
    } else {
      title = 'Missing selected record!';
    }
  }

  return (
    <Dialog fullScreen open onClose={onClose}>
      <Toolbar>
        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
          Page Diff: {title}
        </Typography>
        <IconButton
          data-tip="Go To Previous Difference"
          onClick={async () => {
            navi?.previous();
          }}
        >
          <Icon iconName="chevron-left" />
        </IconButton>
        <IconButton
          data-tip="Go To Next Difference"
          onClick={async () => {
            navi?.next();
          }}
        >
          <Icon iconName="chevron-right" />
        </IconButton>
        <IconButton onClick={onClose}>
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon iconName="times" />
        </IconButton>
      </Toolbar>
      <DiffEditor
        height="100%"
        theme={theme.palette.mode === 'light' ? 'vs' : 'vs-dark'}
        original={original}
        modified={modified}
        language="json"
        options={options}
        onMount={onMount}
      />
    </Dialog>
  );
}
